import type { Revision, RevisionPage } from '../../types';
import { mutations } from '../../utils';
import { getPageAncestorsInRevision } from './getPageAncestorsInRevision';
import { getPageParentInRevision } from './getPageParentInRevision';
import { isRevisionPageGroup, isRevisionPageLink, isRevisionPageParent } from './guards';
import { removePageInRevision } from './removePageInRevision';

/**
 * Check if a page can be moved under another page.
 */
export function isPageMoveableInRevisionUnder(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    underPageId: string
): boolean {
    // Same page
    if (pageId === underPageId) {
        return false;
    }

    const page = mutations.getIn(revision, ['pages', pageId] as const);
    const underPage = mutations.getIn(revision, ['pages', underPageId] as const);

    // One of the page is invalid
    if (!page || !underPage) {
        return false;
    }

    // Links can't have children
    if (isRevisionPageLink(underPage)) {
        return false;
    }

    // Groups can only be in the root
    if (isRevisionPageGroup(page)) {
        return false;
    }

    // Cannot be moved under a descendant
    const underPageAncestors = getPageAncestorsInRevision(revision, underPageId);
    return !(
        mutations.findIndex(
            underPageAncestors,
            (ancestor) =>
                mutations.get(ancestor as mutations.MutableData<RevisionPage>, 'id') === pageId
        ) >= 0
    );
}

/**
 * Check if a page can be moved as a sibling of another page.
 */
export function isPageMoveableInRevisionNextTo(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    nextToPageId: string
): boolean {
    const parent = getPageParentInRevision(revision, nextToPageId);
    return parent
        ? isPageMoveableInRevisionUnder(revision, pageId, mutations.get(parent, 'id'))
        : true;
}

/**
 * Move a page to a new parent and a specific index.
 */
export function movePageInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    newParentId: string | undefined,
    options: {
        /** Index where to move relative to the pages before moving the page. */
        at?: number;
        /** Remove the parent group when it is empty after moving the page */
        deleteEmptyGroupParent?: boolean;
    } = {}
): void {
    const page = mutations.getIn(revision, ['pages', pageId] as const);
    const newParent = newParentId
        ? mutations.getIn(revision, ['pages', newParentId] as const)
        : undefined;

    if (newParent && !isRevisionPageParent(newParent)) {
        throw new Error('Cannot move page under a link');
    }

    if (!page || (newParentId && !isPageMoveableInRevisionUnder(revision, pageId, newParentId))) {
        return;
    }

    const rootPages = mutations.get(revision, 'rootPages');

    // Compute index where to insert
    const newSiblingPagesIds = newParent ? mutations.get(newParent, 'pages') : rootPages;

    let at = typeof options.at === 'number' ? options.at : newSiblingPagesIds.length;

    // Remove from current parent
    const parent = getPageParentInRevision(revision, pageId);
    const currentParentSubpages = parent ? mutations.get(parent, 'pages') : rootPages;
    const removedIndex = mutations.findIndex(
        currentParentSubpages,
        (parentSubpageId) => parentSubpageId === pageId
    );

    const inSameParent = parent ? mutations.get(parent, 'id') === newParentId : !newParentId;

    if (at === removedIndex && inSameParent) {
        // Moving at the same index in the same parent, nothing to do
        return;
    }

    if (removedIndex >= 0) {
        // Remove it
        mutations.delArrayIndex(currentParentSubpages, removedIndex, 1);

        // If moving in same parent, we need to move the index
        if (inSameParent) {
            at = removedIndex <= at ? at - 1 : at;
        }
    }

    // Insert in new parent
    mutations.insert(newSiblingPagesIds, at, [pageId]);

    // If previous parent was a page group and it's now empty, remove it.
    if (
        options.deleteEmptyGroupParent &&
        parent &&
        isRevisionPageGroup(parent) &&
        mutations.get(parent, 'pages').length === 0
    ) {
        removePageInRevision(revision, mutations.get(parent, 'id'));
    }
}

/**
 * Returns the new page index & parent for the movePageBeforeInRevision & movePageAfterInRevision ops.
 */
function getMovePageInRevisionIndexAndParent(
    revision: mutations.MutableData<Revision>,
    afterOrBefore: 'after' | 'before',
    targetPageId: string
): { at: number; parentId?: string } {
    const parent = getPageParentInRevision(revision, targetPageId);
    const newSiblingPagesIds = parent
        ? mutations.get(parent, 'pages')
        : mutations.get(revision, 'rootPages');
    const index = mutations.findIndex(
        newSiblingPagesIds,
        (siblingId) => siblingId === targetPageId
    );

    return {
        at: afterOrBefore === 'after' ? index + 1 : index,
        parentId: parent ? mutations.get(parent, 'id') : undefined,
    };
}

/**
 * Move a page before another one.
 */
export function movePageBeforeInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    beforeId: string
): void {
    const { at, parentId } = getMovePageInRevisionIndexAndParent(revision, 'before', beforeId);

    if (at < 0) {
        return;
    }

    movePageInRevision(revision, pageId, parentId, { at });
}

/**
 * Move a page after another one.
 */
export function movePageAfterInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    afterId: string
): void {
    const { at, parentId } = getMovePageInRevisionIndexAndParent(revision, 'after', afterId);

    if (at < 0) {
        return;
    }

    movePageInRevision(revision, pageId, parentId, { at });
}
